import { routing } from "@/i18n/routing"

/** Locale-scoped paths so auth cookies are not sent for unrelated domain paths. */
const COOKIE_PATHS = routing.locales.map((locale) => `/${locale}`)

/** Also clear legacy Path=/ cookies set before the scope fix. */
const DELETE_PATHS = [...COOKIE_PATHS, "/"]

function cookieFlags(): string {
  const isSecure =
    typeof window !== "undefined" && window.location.protocol === "https:"
  return `;SameSite=Strict${isSecure ? ";Secure" : ""}`
}

function clearLegacyRootCookie(name: string, flags: string): void {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/${flags}`
}

export function setCookie(name: string, value: string, days = 30): void {
  if (typeof document === "undefined") return
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  const flags = cookieFlags()
  const encoded = encodeURIComponent(value)
  clearLegacyRootCookie(name, flags)
  for (const path of COOKIE_PATHS) {
    document.cookie = `${name}=${encoded};expires=${expires.toUTCString()};path=${path}${flags}`
  }
}

/** Set a cookie with max-age in seconds (correct for short-lived auth tokens). */
export function setCookieMaxAge(
  name: string,
  value: string,
  maxAgeSeconds: number
): void {
  if (typeof document === "undefined") return
  const flags = cookieFlags()
  const encoded = encodeURIComponent(value)
  const maxAge = Math.max(0, Math.floor(maxAgeSeconds))
  clearLegacyRootCookie(name, flags)
  for (const path of COOKIE_PATHS) {
    document.cookie = `${name}=${encoded};max-age=${maxAge};path=${path}${flags}`
  }
}

export function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null
  const nameEQ = `${name}=`
  const ca = document.cookie.split(";")
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === " ") c = c.substring(1)
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length))
    }
  }
  return null
}

export function deleteCookie(name: string): void {
  if (typeof document === "undefined") return
  const flags = cookieFlags()
  for (const path of DELETE_PATHS) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=${path}${flags}`
  }
}
